import type {
  DocumentHistory,
  GetDocumentByPathQuery,
} from '#src/graphql/generated';

import { useQuery } from '@apollo/client/react';
import {
  Box,
  HStack,
  Button,
  VStack,
  Spinner,
  Text,
  Heading,
} from '@chakra-ui/react';
import { diffWords } from 'diff';
import { useNavigate, useLocation } from 'react-router-dom';

import { PageHeaderPortal } from '#src/components/page-header-portal';
import { GET_DOCUMENT_HISTORY } from '#src/graphql/queries/document';
import { formatDocumentName } from '#src/utils/format-document-name';

/** Document branch only (parent passes null for DocumentNotFound). */
type DocumentFromQuery = Extract<
  GetDocumentByPathQuery['documentByPath'],
  { __typename: 'Document' }
> | null;

interface DocumentCompareContentProps {
  /** Document from parent query; null when not found (DocumentNotFound). */
  document: DocumentFromQuery;
  /** Canonical path (document.path when document exists, else requested path from URL). */
  path: string;
  fromId: string;
  toId: string;
}

function DocumentCompareContent({
  document,
  path,
  fromId,
  toId,
}: DocumentCompareContentProps) {
  const navigate = useNavigate();

  const { data: fromData, loading: fromLoading } = useQuery(
    GET_DOCUMENT_HISTORY,
    {
      variables: { id: fromId },
      skip: !fromId || fromId === 'current',
    },
  );

  const { data: toData, loading: toLoading } = useQuery(GET_DOCUMENT_HISTORY, {
    variables: { id: toId },
    skip: !toId || toId === 'current',
  });

  const formattedName = document
    ? formatDocumentName(document.path ?? document.id ?? '').join(' > ')
    : null;

  if (!document) {
    return (
      <Box bg="bg" shadow="lg" borderRadius="lg" p={12}>
        <VStack gap={4}>
          <Text fontSize="lg" color="fg.muted" textAlign="center">
            Document not found
          </Text>
          <Button
            colorScheme="blue"
            onClick={() => navigate(`/documents/${path}`)}
          >
            Back to Document
          </Button>
        </VStack>
      </Box>
    );
  }

  const getHistoryValue = (
    historyEntry: DocumentHistory | null | undefined,
  ) => {
    if (!historyEntry) return '';
    const historyData = historyEntry.value as any;
    if (historyData?.value?.value) {
      return historyData.value.value;
    }
    if (typeof historyData === 'string') {
      return historyData;
    }
    return JSON.stringify(historyData, null, 2);
  };

  const getCurrentValue = () => {
    return document.value || '';
  };

  // Determine which values to compare
  const fromValue =
    fromId === 'current'
      ? getCurrentValue()
      : getHistoryValue(fromData?.documentHistory || null);
  const toValue =
    toId === 'current'
      ? getCurrentValue()
      : getHistoryValue(toData?.documentHistory || null);

  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleString();
  };

  const getFromLabel = () => {
    if (fromId === 'current') return 'Current';
    const entry = fromData?.documentHistory;
    return entry ? formatDate(entry.createdAt) : 'Unknown';
  };

  const getToLabel = () => {
    if (toId === 'current') return 'Current';
    const entry = toData?.documentHistory;
    return entry ? formatDate(entry.createdAt) : 'Unknown';
  };

  if (fromLoading || toLoading) {
    return (
      <Box bg="bg" shadow="lg" borderRadius="lg" p={6}>
        <VStack gap={4} align="stretch">
          <Box width="100%" display="flex" justifyContent="center" py={8}>
            <Spinner size="lg" />
          </Box>
        </VStack>
      </Box>
    );
  }

  // Generate diff
  const diff = diffWords(fromValue, toValue);

  const actionsContent = (
    <HStack gap={2}>
      <Button
        size="sm"
        variant="outline"
        onClick={() => navigate(`/documents/${document.path}/history`)}
      >
        Back to History
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => navigate(`/documents/${document.path}`)}
      >
        View
      </Button>
      <Button
        size="sm"
        colorScheme="blue"
        onClick={() => navigate(`/documents/${document.path}`)}
      >
        Edit
      </Button>
    </HStack>
  );

  return (
    <>
      <PageHeaderPortal>{actionsContent}</PageHeaderPortal>

      <Box bg="bg" shadow="lg" borderRadius="lg" p={6}>
        <VStack gap={4} align="stretch">
          {formattedName && (
            <Heading size="lg" color="fg">
              {formattedName} - Compare
            </Heading>
          )}
          {/* Unified Diff View */}
          <Box
            border="1px solid"
            borderColor="border"
            borderRadius="md"
            overflow="hidden"
          >
            <Box
              bg="bg.subtle"
              px={4}
              py={2}
              borderBottom="1px solid"
              borderColor="border"
            >
              <HStack justify="space-between" align="center">
                <Text fontSize="sm" fontWeight="semibold" color="fg">
                  Diff: {getFromLabel()} → {getToLabel()}
                </Text>
              </HStack>
            </Box>
            <Box
              p={4}
              maxH="400px"
              overflow="auto"
              fontFamily="monospace"
              fontSize="sm"
              bg="bg"
              whiteSpace="pre-wrap"
              lineHeight="1.6"
            >
              {diff.map((part, index) => {
                const color = part.added
                  ? 'green.100'
                  : part.removed
                    ? 'red.100'
                    : 'transparent';
                const textColor = part.added
                  ? 'green.800'
                  : part.removed
                    ? 'red.800'
                    : 'fg';
                return (
                  <Text
                    key={index}
                    as="span"
                    bg={color}
                    color={textColor}
                    fontWeight={
                      part.added || part.removed ? 'semibold' : 'normal'
                    }
                  >
                    {part.value}
                  </Text>
                );
              })}
            </Box>
          </Box>
        </VStack>
      </Box>
    </>
  );
}

interface DocumentCompareProps {
  /** Document from parent query; null when not found. */
  document: DocumentFromQuery;
  /** Canonical path (document.path when document exists, else requested path from URL). */
  path: string;
  refetch: () => Promise<unknown>;
}

export function DocumentCompare({ document, path }: DocumentCompareProps) {
  const location = useLocation();

  // Parse from and to from the URL path
  // Format: /documents/{path}/history/compare/{from}/{to}
  const compareMatch = location.pathname.match(
    /\/history\/compare\/([^/]+)\/([^/]+)$/,
  );

  const from = compareMatch ? compareMatch[1] : null;
  const to = compareMatch ? compareMatch[2] : null;

  if (!from || !to) {
    return (
      <Box bg="bg" shadow="lg" borderRadius="lg" p={6}>
        <VStack gap={4}>
          <Text fontSize="lg" color="fg.muted" textAlign="center">
            Invalid compare parameters
          </Text>
          <Text fontSize="sm" color="fg.subtle" textAlign="center">
            Expected format:
            /documents/&#123;path&#125;/history/compare/&#123;from&#125;/&#123;to&#125;
          </Text>
          <Text
            fontSize="xs"
            color="fg.subtle"
            textAlign="center"
            fontFamily="monospace"
          >
            Current path: {location.pathname}
          </Text>
          <Button colorScheme="blue" onClick={() => window.history.back()}>
            Go Back
          </Button>
        </VStack>
      </Box>
    );
  }

  return (
    <DocumentCompareContent
      document={document}
      path={path}
      fromId={from}
      toId={to}
    />
  );
}
